import * as Sentry from "@sentry/vue";

const PROXY_URL = "/api/gladia_sessions";
const TARGET_SAMPLE_RATE = 16000;
const BUFFER_SIZE = 4096;
const STOP_TIMEOUT_MS = 8000;
const INIT_TIMEOUT_MS = 8000;

export const MAX_RECORDING_MS = 5 * 60 * 1000;

/** What is settled, and the words Gladia is still revising. */
export type TranscriptParts = { confirmed: string; hypothesis: string };

type TranscriptCallback = (parts: TranscriptParts) => void;

type GladiaMessage = {
  type?: string;
  data?: {
    id?: string;
    is_final?: boolean;
    utterance?: { text?: string };
    transcription?: { full_transcript?: string };
  };
};

// Simplified port of chat/src/services/gladiaLiveSession.js (no pause/resume).
// Streams 16 kHz PCM16 mic audio to a Gladia live session created through the
// Rails proxy, so the API key stays server-side.
export default class GladiaLiveSession {
  // Survey chat token, lets the server attach the survey's custom vocabulary
  private chatToken: string | null;
  private partialTranscripts: boolean;
  private socket: WebSocket | null = null;
  private sessionId: string | null = null;
  // AudioContext is borrowed, NOT owned - caller closes it
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  // One entry per utterance, keyed by the id Gladia reuses across a partial and
  // its final. Every message *replaces* its entry: partials are the utterance
  // so far, never an increment to append.
  private utterances = new Map<string, { text: string; final: boolean }>();
  private finalTranscript: string | null = null;
  private transcriptCallback: TranscriptCallback | null = null;
  private resolveStop: ((text: string) => void) | null = null;
  private stopTimeout: ReturnType<typeof setTimeout> | null = null;
  // null = live streaming, array = buffering chunks captured before the WS opens
  private pendingChunks: ArrayBuffer[] | null = null;
  // stop() was called before the WS opened - signal end-of-speech as soon as it does
  private stopRequested = false;
  private destroyed = false;
  private initAbortController: AbortController | null = null;

  constructor({ chatToken = null, partialTranscripts = false }: { chatToken?: string | null; partialTranscripts?: boolean } = {}) {
    this.chatToken = chatToken;
    this.partialTranscripts = partialTranscripts;
  }

  // Everything up to the first utterance Gladia has not settled is confirmed;
  // the rest is the hypothesis. Split on the first rather than on each entry's
  // flag, so a late final never reorders words already on screen.
  private get parts(): TranscriptParts {
    const texts = [...this.utterances.values()];
    const firstOpen = texts.findIndex((utterance) => !utterance.final);
    const upTo = firstOpen === -1 ? texts.length : firstOpen;
    const join = (slice: typeof texts) =>
      slice
        .map((utterance) => utterance.text)
        .filter(Boolean)
        .join(" ");

    return { confirmed: join(texts.slice(0, upTo)), hypothesis: join(texts.slice(upTo)) };
  }

  private get transcript(): string {
    const { confirmed, hypothesis } = this.parts;
    return [confirmed, hypothesis].filter(Boolean).join(" ");
  }

  onTranscript(fn: TranscriptCallback) {
    this.transcriptCallback = fn;
  }

  async start(stream: MediaStream, audioContext: AudioContext) {
    this.audioContext = audioContext;

    // Capture into a buffer immediately so no speech is lost while the
    // network round-trips to create the Gladia session.
    this.pendingChunks = [];
    this.setupAudioPipeline(stream);

    const { url, id } = await this.initSession();

    // destroy() fired while the fetch was in flight - delete the
    // just-created session so it isn't orphaned on Gladia's side.
    if (this.destroyed) {
      fetch(`${PROXY_URL}/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
      return;
    }

    this.sessionId = id;
    this.socket = await this.connectWebSocket(url);

    if (this.destroyed) {
      this.socket.close(1000);
      this.socket = null;
      this.deleteTranscript();
      return;
    }

    this.flushPendingChunks();

    if (this.stopRequested && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "stop_recording" }));
    }
  }

  // Signals end-of-speech and resolves with the best available transcript
  // (final if Gladia answers in time, accumulated partials otherwise).
  stop(): Promise<string> {
    this.disconnectAudioPipeline();
    this.stopRequested = true;

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "stop_recording" }));
    }

    if (this.finalTranscript !== null) {
      return Promise.resolve(this.finalTranscript);
    }

    return new Promise((resolve) => {
      this.stopTimeout = setTimeout(() => {
        this.resolveStop = null;
        resolve(this.transcript);
      }, STOP_TIMEOUT_MS);

      this.resolveStop = (text) => {
        if (this.stopTimeout) clearTimeout(this.stopTimeout);
        this.stopTimeout = null;
        this.resolveStop = null;
        resolve(text);
      };
    });
  }

  // Full cleanup: close WS, disconnect audio graph, GDPR-delete the transcript.
  // Safe to call multiple times.
  destroy() {
    this.destroyed = true;

    if (this.initAbortController) {
      this.initAbortController.abort();
      this.initAbortController = null;
    }

    this.pendingChunks = null;
    this.disconnectAudioPipeline();

    if (this.resolveStop) {
      this.resolveStop(this.transcript);
    }
    if (this.stopTimeout) {
      clearTimeout(this.stopTimeout);
      this.stopTimeout = null;
    }

    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close(1000);
      }
      this.socket = null;
    }

    this.deleteTranscript();
  }

  private async initSession(): Promise<{ url: string; id: string }> {
    this.initAbortController = new AbortController();
    const fetchTimeout = setTimeout(() => this.initAbortController?.abort(), INIT_TIMEOUT_MS);

    const body: Record<string, unknown> = { sample_rate: TARGET_SAMPLE_RATE };
    if (this.chatToken) body.chat_token = this.chatToken;
    if (this.partialTranscripts) body.partial_transcripts = true;

    try {
      const res = await fetch(PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: this.initAbortController.signal,
      });

      if (!res.ok) throw new Error(`Gladia session init failed (${res.status})`);

      return res.json();
    } finally {
      clearTimeout(fetchTimeout);
      this.initAbortController = null;
    }
  }

  private connectWebSocket(url: string): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);

      const connectTimeout = setTimeout(() => {
        ws.close();
        reject(new Error("Gladia WebSocket connection timed out"));
      }, INIT_TIMEOUT_MS);

      ws.onopen = () => {
        clearTimeout(connectTimeout);
        resolve(ws);
      };

      ws.onerror = () => {
        clearTimeout(connectTimeout);
        reject(new Error("Gladia WebSocket connection failed"));
      };

      ws.onmessage = (event) => this.handleMessage(event);

      // Network drop / server timeout: resolve any pending stop() with what we have
      ws.onclose = () => {
        if (this.resolveStop) {
          this.resolveStop(this.transcript);
        }
      };
    });
  }

  private handleMessage(event: MessageEvent) {
    if (this.destroyed) return;
    try {
      const payload: GladiaMessage = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      if (payload?.type === "transcript" && payload?.data?.utterance?.text) {
        const isFinal = payload.data.is_final !== false;
        const id = payload.data.id || String(this.utterances.size);
        this.utterances.set(id, { text: payload.data.utterance.text, final: isFinal });
        if (this.transcriptCallback) this.transcriptCallback(this.parts);
      }

      if (payload?.type === "post_final_transcript") {
        this.finalTranscript = payload?.data?.transcription?.full_transcript || this.transcript;
        if (this.transcriptCallback) this.transcriptCallback({ confirmed: this.finalTranscript, hypothesis: "" });
        if (this.resolveStop) this.resolveStop(this.finalTranscript);
      }
    } catch {
      // Ignore malformed messages
    }
  }

  // Hooks a ScriptProcessor onto the shared AudioContext and resamples from
  // the context's native sampleRate to 16 kHz PCM16 before sending to Gladia.
  private setupAudioPipeline(stream: MediaStream) {
    if (!this.audioContext) return;

    const nativeRate = this.audioContext.sampleRate;
    const ratio = TARGET_SAMPLE_RATE / nativeRate;

    this.source = this.audioContext.createMediaStreamSource(stream);
    this.processor = this.audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);

    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);

    this.processor.onaudioprocess = (e) => {
      // Always zero the output to prevent mic audio leaking to speakers
      e.outputBuffer.getChannelData(0).fill(0);

      const input = e.inputBuffer.getChannelData(0);

      // Downsample via linear interpolation: nativeRate -> 16 kHz
      const outputLength = Math.round(input.length * ratio);
      const pcm16 = new Int16Array(outputLength);
      for (let i = 0; i < outputLength; i += 1) {
        const srcIdx = i / ratio;
        const lo = Math.floor(srcIdx);
        const hi = Math.min(lo + 1, input.length - 1);
        const frac = srcIdx - lo;
        const sample = input[lo] * (1 - frac) + input[hi] * frac;
        pcm16[i] = Math.max(-1, Math.min(1, sample)) * 0x7fff;
      }

      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(pcm16.buffer);
      } else if (Array.isArray(this.pendingChunks)) {
        // Buffer while WS is connecting - slice() copies the buffer since it's reused by the browser
        this.pendingChunks.push(pcm16.buffer.slice(0));
      }
    };
  }

  private flushPendingChunks() {
    const chunks = this.pendingChunks;
    // Switch to live mode before sending so no race with onaudioprocess
    this.pendingChunks = null;
    if (!chunks || !this.socket) return;
    chunks.forEach((chunk) => {
      if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(chunk);
    });
  }

  // Disconnects processor & source. AudioContext is NOT closed (caller owns it).
  private disconnectAudioPipeline() {
    if (this.processor) {
      this.processor.onaudioprocess = null;
      try {
        this.processor.disconnect();
      } catch {
        // Already disconnected
      }
      this.processor = null;
    }
    if (this.source) {
      try {
        this.source.disconnect();
      } catch {
        // Already disconnected
      }
      this.source = null;
    }
    this.audioContext = null;
  }

  // Best-effort GDPR delete of the Gladia transcript via the Rails proxy.
  private async deleteTranscript() {
    const id = this.sessionId;
    this.sessionId = null;
    if (!id) return;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const res = await fetch(`${PROXY_URL}/${id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        });
        if (res.ok || res.status === 202) return;
        throw new Error(`GDPR delete failed (${res.status})`);
      } catch (err) {
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, attempt * 500));
        } else {
          const message = err instanceof Error ? err.message : String(err);
          Sentry.captureException(
            new Error(`[Gladia] GDPR delete failed after 3 attempts (session ${id}): ${message}`),
          );
        }
      }
    }
  }
}
